/**
 * The phone as a remote.
 *
 * ## What it is for
 *
 * Not "the same buttons, on glass". The physical remote is better at play and pause than a
 * touchscreen will ever be — it is in your hand and you do not have to look at it. What a
 * phone is better at is everything the remote is bad at: knowing what is playing without
 * turning the TV on, scrubbing to a position instead of holding a key, and starting
 * something without typing its name four arrow presses at a time.
 *
 * So the layout leads with the now-playing card and the scrubber, and the transport
 * buttons are underneath rather than the point.
 *
 * ## It shows only what the television said
 *
 * Every fact here arrives as a `state` message from the TV. Nothing is inferred from what
 * this phone last did — a remote that showed "playing" because it sent play a moment ago
 * would be confidently wrong exactly when the TV refused, which is when the viewer looks
 * at it.
 */
import { useEffect, useMemo, useState } from 'react';
import type { CatalogItemDto, RemoteTracksDto } from '@filmodasi/shared';
import { api } from '../api/client';
import { Empty, Spinner } from '../components/ui';
import { Icon } from '../components/Icon';
import { useRealtimeChannel } from '../realtime/RealtimeContext';
import { JogDial } from '../components/JogDial';
import type { PresenceDevice } from '../realtime/useRealtime';

interface TvState {
  deviceId: string;
  itemId: string | null;
  playbackState: 'playing' | 'paused' | 'stopped';
  positionSec: number | null;
  durationSec: number | null;
  /** Null when the television's engine cannot enumerate tracks, or has none worth offering. */
  tracks: RemoteTracksDto | null;
}

function clock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(rest).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

export function RemotePage() {
  const { status, presence, send, subscribe } = useRealtimeChannel();
  const [state, setState] = useState<TvState | null>(null);
  const [item, setItem] = useState<CatalogItemDto | null>(null);
  /*
   * Held while the finger is down, so the bar follows the thumb instead of snapping back
   * each time the television's five-second position report arrives.
   */
  const [scrubbing, setScrubbing] = useState<number | null>(null);

  const televisions = useMemo(
    () => presence.filter((d: PresenceDevice) => d.deviceType === 'tv'),
    [presence],
  );
  const target = state?.deviceId ?? televisions[0]?.deviceId ?? null;

  useEffect(
    () =>
      subscribe((message) => {
        if (message.type !== 'state') return;
        setState({
          deviceId: message.deviceId,
          itemId: message.itemId,
          playbackState: message.playbackState,
          positionSec: message.positionSec,
          durationSec: message.durationSec,
          tracks: message.tracks ?? null,
        });
      }),
    [subscribe],
  );

  /* -- resolve the title from the catalogue rather than over the wire -- */
  useEffect(() => {
    const id = state?.itemId;
    if (!id) {
      setItem(null);
      return;
    }
    if (item?.id === id) return;

    let cancelled = false;
    api<CatalogItemDto>(`/api/catalog/item/${id}`)
      .then((found) => {
        if (!cancelled) setItem(found);
      })
      .catch(() => {
        // A title that cannot be resolved is not worth an error on a remote; the controls
        // still work and the card simply says less.
        if (!cancelled) setItem(null);
      });
    return () => {
      cancelled = true;
    };
  }, [state?.itemId, item?.id]);

  const command = (name: string, value?: number) => {
    if (!target) return;
    send({ type: 'command', targetDeviceId: target, command: name, ...(value === undefined ? {} : { value }) });
  };

  /**
   * Asks the television to switch a track.
   *
   * The id goes across as the television reported it and is checked there against the
   * engine's own list, so a stale one — the list changed while the sheet was open —
   * selects nothing rather than the wrong thing.
   *
   * Nothing is written to `state` here. The television answers with its next report, which
   * is what moves the tick: a local guess would show the switch as done on an engine that
   * refused it, and AVPlay does refuse.
   */
  const selectTrack = (kind: 'audio' | 'subtitle', id: string | null) => {
    if (!target) return;
    send({
      type: 'command',
      targetDeviceId: target,
      command: kind === 'audio' ? 'audioTrack' : 'subtitleTrack',
      ...(id === null ? {} : { trackId: id }),
    });
  };

  if (status !== 'open') {
    return (
      <main className="remote">
        <h1>Kumanda</h1>
        <Spinner label="Bağlanılıyor…" />
      </main>
    );
  }

  if (televisions.length === 0 && !state) {
    return (
      <main className="remote">
        <h1>Kumanda</h1>
        <Empty>
          Açık bir televizyon yok. Televizyonda Filmodasi'yi açtığınızda burada görünür.
        </Empty>
      </main>
    );
  }

  const duration = state?.durationSec ?? 0;
  const position = scrubbing ?? state?.positionSec ?? 0;
  const playing = state?.playbackState === 'playing';
  const nothingOn = !state || state.playbackState === 'stopped' || state.itemId === null;

  return (
    <main className="remote">
      <h1>Kumanda</h1>

      <section className="remote__now">
        {item?.poster || item?.logo ? (
          <img className="remote__art" src={item.poster ?? item.logo ?? ''} alt="" />
        ) : (
          <div className="remote__art remote__art--empty" aria-hidden />
        )}
        <div className="remote__meta">
          <strong>{nothingOn ? 'Televizyonda bir şey oynatılmıyor' : (item?.title ?? 'Oynatılıyor')}</strong>
          {!nothingOn && (
            <span className="remote__state">{playing ? 'Oynatılıyor' : 'Duraklatıldı'}</span>
          )}
        </div>
      </section>

      {/*
        The remote itself.

        Drawn as an object rather than a toolbar: a moulded body, a recessed dial and keys
        that sit proud of the surface. It is the one screen in the app that pretends to be
        a thing, and it earns that — a remote you can work by shape is a remote you can use
        without taking your eyes off the television, which is the entire point of holding
        one.
      */}
      <section className="handset">
        <div className="handset__readout" aria-hidden>
          <span>{clock(position)}</span>
          <span className="handset__readoutRight">
            {duration > 0 ? clock(duration) : nothingOn ? '—' : 'CANLI'}
          </span>
        </div>

        <JogDial
          position={position}
          duration={duration}
          // Live has no position to turn to, and neither does a stopped television. The
          // dial goes dead rather than turning smoothly against nothing.
          disabled={nothingOn || duration <= 0}
          onPreview={setScrubbing}
          onCommit={(seconds) => {
            command('seek', Math.round(seconds));
            setScrubbing(null);
          }}
        />

        <div className="handset__keys">
          <button
            type="button"
            className="key"
            onClick={() => command('previous')}
            aria-label="10 saniye geri"
          >
            <Icon name="rewind" size={1.3} />
          </button>
          <button
            type="button"
            className="key key--play"
            onClick={() => command(playing ? 'pause' : 'play')}
            aria-label={playing ? 'Duraklat' : 'Oynat'}
          >
            <Icon name={playing ? 'pause' : 'play'} size={1.4} />
          </button>
          <button
            type="button"
            className="key"
            onClick={() => command('next')}
            aria-label="10 saniye ileri"
          >
            <Icon name="forward" size={1.3} />
          </button>
        </div>

        <div className="handset__keys handset__keys--small">
          <button type="button" className="key key--flat" onClick={() => command('back')}>
            Geri
          </button>
          <button type="button" className="key key--flat" onClick={() => command('home')}>
            Ana Sayfa
          </button>
          <button
            type="button"
            className="key key--flat key--stop"
            onClick={() => command('stop')}
            disabled={nothingOn}
          >
            Durdur
          </button>
        </div>

        {/*
          Tracks, as visible rows rather than a menu behind a button.

          A remote is worked by glance and thumb. Hiding "which audio is playing" one tap
          down means the answer to the question that prompted picking up the phone —
          "why is this in English" — is not on the screen. There are rarely more than three
          of each, so the whole answer fits.

          Rendered only when the television says there is a choice. `publishable()` on the
          other end returns null both for an engine that cannot enumerate and for a stream
          with one audio track and no subtitles, so a section here always has something in
          it. A permanent "your television cannot do this" note on every film would be
          noise on the one screen that has to stay glanceable.
        */}
        {state?.tracks && (
          <div className="handset__tracks">
            {state.tracks.audio.length > 1 && (
              <TrackRow
                icon="audio"
                title="Ses"
                rows={state.tracks.audio}
                active={state.tracks.activeAudio}
                onPick={(id) => selectTrack('audio', id)}
              />
            )}
            {state.tracks.subtitle.length > 0 && (
              <TrackRow
                icon="subtitles"
                title="Altyazı"
                // "Kapalı" is one of the answers, not the absence of one, so it is a row
                // and it is tickable — the viewer can see that off is what is true now.
                // Carried as a real `null` rather than a sentinel string: the protocol
                // does not forbid an empty track id, so `''` would be a collision waiting
                // for a stream odd enough to produce one.
                rows={[{ id: null, label: 'Kapalı' }, ...state.tracks.subtitle]}
                active={state.tracks.activeSubtitle ?? null}
                onPick={(id) => selectTrack('subtitle', id)}
              />
            )}
          </div>
        )}
      </section>
    </main>
  );
}

/**
 * One kind of track, as a wrapping row of chips.
 *
 * The selected chip is marked by colour *and* by a tick. Colour alone fails twice here:
 * over the handset's moulded surface the accent is not as loud as it is elsewhere, and
 * this is a control people use in a dark room while looking somewhere else.
 */
function TrackRow({
  icon,
  title,
  rows,
  active,
  onPick,
}: {
  icon: 'audio' | 'subtitles';
  title: string;
  /** `id: null` is the subtitles' "off" row; every real track has one. */
  rows: { id: string | null; label: string }[];
  active: string | null;
  onPick: (id: string | null) => void;
}) {
  return (
    /*
     * A named group, and not for form's sake.
     *
     * The same label appears in both sections whenever a film carries Turkish audio and
     * Turkish subtitles, which is most of this catalogue. Visually the heading above each
     * row settles it; to a screen reader the two chips are indistinguishable — "Türkçe,
     * pressed" twice, with no way to tell which one is the audio. The group name is what
     * carries the heading into the accessibility tree.
     */
    <div className="tracks" role="group" aria-label={title}>
      <span className="tracks__label">
        <Icon name={icon} size={0.95} />
        {title}
      </span>
      <div className="tracks__chips">
        {rows.map((row) => {
          const selected = row.id === active;
          return (
            <button
              key={row.id ?? 'off'}
              type="button"
              className={`tracks__chip${selected ? ' is-selected' : ''}`}
              aria-pressed={selected}
              onClick={() => onPick(row.id)}
            >
              {selected && <Icon name="check" size={0.8} />}
              {row.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default RemotePage;
